import { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Link2, Image as ImageIcon, X } from "lucide-react";
import { supabase, formatSupabaseError } from "../lib/supabase";

const BUCKET = "site-images";
const MAX_BYTES = 8 * 1024 * 1024; // 8 MB

const slugify = (s) =>
  (s || "image")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);

/**
 * Image picker for the admin: paste URL or upload a file.
 * Uploaded files land in Supabase Storage bucket `site-images` and we
 * write the resulting public URL into the JSON content.
 */
export const ImageUpload = ({ value, onChange, label = "Bild", testid }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [mode, setMode] = useState("upload"); // 'upload' | 'url'

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Bitte eine Bilddatei wählen (JPG, PNG, WebP, GIF).");
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error(`Datei zu groß. Max. 8 MB. (Aktuell: ${(file.size / 1024 / 1024).toFixed(1)} MB)`);
      return;
    }
    setUploading(true);
    try {
      const ext = (file.name.split(".").pop() || "jpg").toLowerCase();
      const path = `${slugify(label)}/${Date.now()}-${slugify(file.name.replace(/\.[^.]+$/, ""))}.${ext}`;
      const { error: upErr } = await supabase.storage
        .from(BUCKET)
        .upload(path, file, {
          cacheControl: "31536000",
          upsert: false,
          contentType: file.type,
        });
      if (upErr) throw upErr;
      const { data: pub } = supabase.storage.from(BUCKET).getPublicUrl(path);
      const url = pub?.publicUrl;
      if (!url) throw new Error("Konnte öffentliche URL nicht erzeugen.");
      onChange(url);
      toast.success("Bild hochgeladen.");
    } catch (err) {
      const msg = formatSupabaseError(err);
      if (msg.includes("Bucket not found") || msg.includes("not found")) {
        toast.error("Storage Bucket 'site-images' fehlt. Bitte zuerst Migration in Supabase ausführen.");
      } else {
        toast.error(msg);
      }
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="mb-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold">{label}</span>
        <div className="flex items-center gap-1 text-[9px] tracking-[0.2em] uppercase">
          <button
            type="button"
            onClick={() => setMode("upload")}
            className={`px-2 py-1 border transition-colors ${mode === "upload" ? "border-[#ff0033]/60 text-white" : "border-white/10 text-zinc-500 hover:text-zinc-300"}`}
          >
            <Upload size={10} className="inline mr-1" /> Upload
          </button>
          <button
            type="button"
            onClick={() => setMode("url")}
            className={`px-2 py-1 border transition-colors ${mode === "url" ? "border-[#ff0033]/60 text-white" : "border-white/10 text-zinc-500 hover:text-zinc-300"}`}
          >
            <Link2 size={10} className="inline mr-1" /> URL
          </button>
        </div>
      </div>

      {mode === "upload" ? (
        <div>
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            data-testid={testid ? `${testid}-file` : undefined}
            onChange={(e) => handleFile(e.target.files?.[0])}
            className="hidden"
          />
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            data-testid={testid ? `${testid}-trigger` : undefined}
            className="w-full flex items-center justify-center gap-2 bg-[#050505] border border-dashed border-white/15 hover:border-[#ff0033]/50 text-sm text-zinc-300 hover:text-white p-3 transition-colors disabled:opacity-50"
          >
            <ImageIcon size={14} className="text-[#ff0033]" />
            {uploading ? "Lädt hoch…" : value ? "Bild austauschen" : "Bild auswählen (max 8 MB)"}
          </button>
        </div>
      ) : (
        <input
          type="text"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="https://…"
          data-testid={testid}
          className="w-full bg-[#050505] border border-white/10 focus:border-[#ff0033]/60 outline-none text-white text-sm p-3 font-medium transition-colors"
        />
      )}

      {value && (
        <div className="relative mt-3 inline-block">
          <img src={value} alt="" className="max-h-40 border border-white/10" />
          <button
            type="button"
            onClick={() => onChange("")}
            aria-label="Bild entfernen"
            className="absolute -top-2 -right-2 w-6 h-6 inline-flex items-center justify-center bg-[#0A0A0A] border border-white/20 hover:border-[#ff0033]/60 text-zinc-300 hover:text-white"
          >
            <X size={12} />
          </button>
        </div>
      )}
    </div>
  );
};

export default ImageUpload;
